import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Chatbox, ConversationBuilder, Session } from 'talkjs/all';
import { Box, Text } from '@rsos/components/capstone/base';
import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import useTF911LoggedInUser from '@rsos/hooks/talkjs/useTF911LoggedInUser';
import {
  addParticipant,
  setConversationUnread,
} from '@rsos/reduxDataStore/src/features/conversations/conversationsSlice';
import {
  useGetConversationID,
  useGetConversationSource,
  useHasUnreadConversationMessages,
  useIsConversationEnded,
} from '@rsos/reduxDataStore/src/features/conversations/utils';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { type PreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesAPI';
import { buildDoNotTranslateSendOptions } from '@rsos/utils/SmartPhrases';
import { createTalkSession, createTalkUser } from '@rsos/utils/talkJS';
import useTalkJSTheme from '../../../../hooks/useTalkJSTheme';
import SuggestedResponses from '../../../PreconfiguredMessages/SuggestedResponses';
import RequestLocation from '../../../RequestLocation';
import { useChatContext } from '../ChatContext';
import { OutgoingTranslationWarning } from '../SharedChatUI/OutgoingTranslationWarning';
import { useRequestLocation } from '../useRequestLocation';
import { useTrackSentLocationRequests } from '../useTrackSentLocationRequests';
import { ChatContainer } from './ConversationsChatbox.styles';

interface ConversationsChatProps {
  hasRequestLocationAccess: boolean;
  preconfiguredMessages?: PreconfiguredResponse[];
}

const ConversationsChat = ({
  hasRequestLocationAccess,
  preconfiguredMessages = [],
}: ConversationsChatProps) => {
  const {
    isCollapsed,
    setIsAllMessagesVisible,
    selectedPreconfiguredMessage,
    setSelectedPreconfiguredMessage,
    locationRequestPathway,
  } = useChatContext();
  const callerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );
  const source = useGetConversationSource(callerID);
  const dispatch = useDispatch();
  const chatRef = useRef<HTMLDivElement>(null);
  const [chatSession, setChatSession] = useState<Session | null>(null);
  const [chatbox, setChatbox] = useState<Chatbox | null>(null);
  const [conversation, setConversation] = useState<ConversationBuilder | null>(
    null,
  );
  const chatTheme = useTalkJSTheme(source === SOURCE_TCAPI ? 'tcapi' : 'tf911');
  const conversationID = useGetConversationID(callerID);
  const isConversationEnded = useIsConversationEnded(callerID);
  const loggedInUser = useTF911LoggedInUser();
  const hasUnreadConversationMessages =
    useHasUnreadConversationMessages(callerID);

  const hasSuggestedResponses =
    !isConversationEnded && !isCollapsed && conversationID;

  const handleViewClick = () => {
    setIsAllMessagesVisible(true);
  };

  const { handleRequestLocation, locationError } = useRequestLocation({
    chat: chatbox,
    isConversationEnded,
    preconfiguredMessages,
    pathwayType: locationRequestPathway,
    conversationId: conversationID ?? undefined,
  });

  useTrackSentLocationRequests({
    chatSession,
    pathwayType: locationRequestPathway,
    callerID,
    conversationId: conversationID ?? undefined,
  });

  // Mount the TalkJS session and chat container
  useEffect(() => {
    let session, chat;
    try {
      (async () => {
        session = await createTalkSession(loggedInUser);

        chat = session.createChatbox({
          theme: chatTheme,
          showChatHeader: false,
          messageField: {
            visible: true,
            placeholder: 'Type Message',
          },
        });

        chat.mount(chatRef.current);

        dispatch(addParticipant({ callerID, participant: loggedInUser }));

        setChatSession(session);

        setChatbox(chat);
      })();
    } catch (error) {
      if (session) {
        setChatbox(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatTheme, loggedInUser, dispatch]);

  // Set Conversation
  useEffect(() => {
    if (chatbox && chatSession && conversationID) {
      const conversation = chatSession.getOrCreateConversation(conversationID);

      conversation.setParticipant(createTalkUser(loggedInUser));

      setConversation(conversation);

      chatbox.select(conversation);

      chatbox.messageField.setVisible(!isConversationEnded);
    }
  }, [
    chatSession,
    chatbox,
    conversationID,
    isConversationEnded,
    loggedInUser,
    callerID,
  ]);

  // Mark chat as read
  useEffect(() => {
    if (hasUnreadConversationMessages) {
      dispatch(setConversationUnread({ callerID, isUnread: false }));
    }
  }, [hasUnreadConversationMessages, callerID, dispatch]);

  // Set messageField text
  useEffect(() => {
    if (
      !chatbox ||
      !conversation ||
      isConversationEnded ||
      !selectedPreconfiguredMessage
    )
      return;

    if (selectedPreconfiguredMessage.isSending) {
      conversation.sendMessage(
        selectedPreconfiguredMessage.message.body,
        buildDoNotTranslateSendOptions(
          selectedPreconfiguredMessage.message.resolvedPhrases,
        ),
      );
      chatbox.messageField.setText('');
    } else {
      chatbox.messageField.setText(selectedPreconfiguredMessage.message.body);
    }
    setSelectedPreconfiguredMessage(null);
  }, [
    selectedPreconfiguredMessage,
    chatbox,
    conversation,
    isConversationEnded,
    setSelectedPreconfiguredMessage,
    dispatch,
  ]);

  return (
    <>
      <ChatContainer ref={chatRef} display={isCollapsed ? 'none' : 'flex'} />
      {!isConversationEnded && <OutgoingTranslationWarning />}
      {hasSuggestedResponses && (
        <>
          <Box width="100%" horizontal align="center">
            {hasRequestLocationAccess && (
              <RequestLocation onClick={handleRequestLocation} />
            )}
            <SuggestedResponses handleViewClick={handleViewClick} />
          </Box>
          {locationError && (
            <Text size="small" color="heavyDanger">
              Something went wrong, please try again
            </Text>
          )}
        </>
      )}
    </>
  );
};

export default ConversationsChat;
